import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import COUNTRY_IMAGES, { getCountryImage } from '../data/countryImages';
import { registerPushForUser, unregisterPushForUser } from '../lib/pushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = [
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'SAR', name: 'Saudi Arabia', symbol: '﷼' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'GBP', name: 'British Pound Sterling', symbol: '£' },
  { code: 'USD', name: 'American Dollar', symbol: '$' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
];

const AppContext = createContext(undefined);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [mySims, setMySims] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [destinationGroups, setDestinationGroups] = useState([]);
  const [startingPrices, setStartingPrices] = useState({});
  const [apiError, setApiError] = useState(null);
  const [livePlanCache, setLivePlanCache] = useState({});
  const [destinationMetaCache, setDestinationMetaCache] = useState({}); // Cache for signals/carriers data
  
  const [markupSettings, setMarkupSettings] = useState({ 
    // Grid: geography x price tier
    local_0_3: 40, local_3_6: 40, local_6_10: 40,
    local_10_15: 40, local_15_20: 40, local_20_plus: 40,
    regional_0_3: 25, regional_3_6: 25, regional_6_10: 25,
    regional_10_15: 25, regional_15_20: 25, regional_20_plus: 25,
    global_0_3: 20, global_3_6: 20, global_6_10: 20,
    global_10_15: 20, global_15_20: 20, global_20_plus: 20
  });
  const [promoCodes, setPromoCodes] = useState([]);
  // markupLoaded starts false until /api/markup-settings responds. Anything
  // that displays or transacts on prices MUST wait for this to prevent the
  // 2026-07-01 incident where users saw prices computed with the hardcoded
  // default markup (25% regional / 40% local / 20% global) instead of the
  // admin-configured grid.
  const [markupLoaded, setMarkupLoaded] = useState(false);
  // Admin-configurable display name overrides ({code: overrideName}).
  // Loaded on app mount; used in buildCountryFromRaw to remap country.name
  // (e.g. Singapore/Singapore Alt, Europe/Europe Plus disambiguation).
  const [nameOverrides, setNameOverrides] = useState({});

  // Admin-configurable region affiliation overrides ({code: region}).
  // Loaded on app mount; used in mapRawToCountry to resolve destination/region
  // mismatches (e.g. force a country under the correct continent grouping).
  const [regionOverrides, setRegionOverrides] = useState({});

  // Admin-configurable destination cover images ({code: image_url}). Loaded on
  // app mount and applied in mapRawToCountry so admin covers show on the public
  // site (web + mobile already consume the same /cover-image-overrides).
  const [coverOverrides, setCoverOverrides] = useState({});

  // Admin-configurable destination tags ({code: [tags]}). Loaded on app mount;
  // rendered as pills on destination cards (in addition to the built-in
  // "Regional" pill on regional plans).
  const [destinationTags, setDestinationTags] = useState({});
  
  const [currentCurrency, setCurrentCurrency] = useState(() => {
    const findCurr = (code) => CURRENCIES.find(c => c.code === code);
    const defaultCurr = findCurr('AUD') || CURRENCIES[0]; // Default to AUD

    // Persisted user choice always wins — if the user has explicitly picked a
    // currency in a previous session/tab, restore it before any timezone
    // heuristics. This fixes a bug (2026-07-21) where selecting USD in the
    // Navbar didn't persist across full page loads/direct URL navigation,
    // causing the Checkout page to fall back to AUD.
    try {
      const saved = localStorage.getItem('connectify_currency');
      if (saved) {
        const found = findCurr(saved);
        if (found) return found;
      }
    } catch (_) { /* ignore storage errors */ }

    try {
      // First, check timezone - this is more reliable than locale
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      
      // Australia check FIRST - highest priority for Australian users
      if (timeZone.includes('Australia')) return findCurr('AUD') || defaultCurr;
      
      // Get user's locale - this gives us country/region info
      const userLocale = navigator.language || navigator.languages?.[0] || '';
      
      // Extract region code from locale (e.g., 'en-US' -> 'US', 'en-GB' -> 'GB')
      const regionCode = userLocale.split('-')[1]?.toUpperCase() || '';
      
      // Map country/region codes to currencies
      const regionToCurrency = {
        // Australia - check first
        AU: 'AUD',
        // Europe (Eurozone)
        AT: 'EUR', BE: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR', FR: 'EUR',
        DE: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR', LV: 'EUR', LT: 'EUR',
        LU: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SK: 'EUR', SI: 'EUR',
        ES: 'EUR', AD: 'EUR', MC: 'EUR', SM: 'EUR', VA: 'EUR',
        // UK
        GB: 'GBP', UK: 'GBP',
        // India
        IN: 'INR',
        // UAE
        AE: 'AED',
        // Saudi Arabia
        SA: 'SAR',
        // China
        CN: 'CNY', HK: 'CNY', TW: 'CNY',
        // Malaysia
        MY: 'MYR',
        // USA
        US: 'USD',
      };
      
      // Check if we have a currency mapping for this region
      if (regionCode && regionToCurrency[regionCode]) {
        return findCurr(regionToCurrency[regionCode]) || defaultCurr;
      }
      
      // Fallback: additional timezone detection for other regions
      if (timeZone.includes('Europe') && !timeZone.includes('London')) return findCurr('EUR') || defaultCurr;
      if (timeZone.includes('London')) return findCurr('GBP') || defaultCurr;
      if (timeZone.includes('Kolkata') || timeZone.includes('India')) return findCurr('INR') || defaultCurr;
      if (timeZone.includes('Dubai')) return findCurr('AED') || defaultCurr;
      if (timeZone.includes('Riyadh')) return findCurr('SAR') || defaultCurr;
      if (timeZone.includes('Shanghai') || timeZone.includes('Hong_Kong')) return findCurr('CNY') || defaultCurr;
      if (timeZone.includes('Kuala_Lumpur')) return findCurr('MYR') || defaultCurr;
      if (timeZone.includes('America/')) return findCurr('USD') || defaultCurr;
      
      // Default to AUD for all other/unknown regions
      return defaultCurr;
    } catch { 
      return defaultCurr; 
    }
  });

  // Persist the user's currency choice so it survives page reloads and
  // direct URL navigation. Companion to the initializer-side restore above.
  useEffect(() => {
    try {
      if (currentCurrency?.code) {
        localStorage.setItem('connectify_currency', currentCurrency.code);
      }
    } catch (_) { /* ignore storage errors */ }
  }, [currentCurrency]);

  // Exchange rates - loaded once per day at 12am AWST
  const [exchangeRates, setExchangeRates] = useState(() => {
    // Try to load cached rates from localStorage
    try {
      const cachedRates = localStorage.getItem('connectify_exchange_rates');
      const cachedDate = localStorage.getItem('connectify_exchange_rates_date');
      
      if (cachedRates && cachedDate) {
        // Get today's date in AWST (UTC+8)
        const now = new Date();
        const awstTime = new Date(now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60000);
        const todayAWST = awstTime.toISOString().split('T')[0];
        
        // Use cached rates if from today
        if (cachedDate === todayAWST) {
          return JSON.parse(cachedRates);
        }
      }
    } catch (e) {
      console.log('Error loading cached rates');
    }
    // Default fallback rates
    return { USD: 1, EUR: 0.92, INR: 83.50, AED: 3.67, AUD: 1.50, SAR: 3.75, CNY: 7.20, GBP: 0.79, MYR: 4.70 };
  });
  
  const [ratesLoaded, setRatesLoaded] = useState(false);

  // Client-side isAdmin is a display-only convenience. Server enforces admin
  // access on every /api/admin/* route via require_admin (SEC-001 remediation).
  const isAdmin = !!user?.isAdmin;

  // Load user session from localStorage on mount. Restore the Authorization
  // header so every axios call inherits it (frontend of SEC-001 fix — the
  // backend now requires a real Bearer token on protected routes).
  useEffect(() => {
    // Cross-app checkout handoff: the native app opens
    // /destinations/{id}?buy=..&st=<session_token>&u=<user_json> so the browser
    // is already signed in and jumps straight to checkout (no login screen).
    let handoffToken = null;
    try {
      const params = new URLSearchParams(window.location.search);
      handoffToken = params.get('st');
      const handoffUser = params.get('u');
      // Embedded (native app) checkout — hide site chrome for the whole session.
      if (params.get('app') === '1') {
        try { sessionStorage.setItem('cf_embedded', '1'); } catch (e) { /* ignore */ }
      }
      // Return URL the native app wants us to bounce back to on success.
      const handoffReturn = params.get('return');
      if (handoffReturn) {
        try { sessionStorage.setItem('cf_return', handoffReturn); } catch (e) { /* ignore */ }
      }
      if (handoffToken) {
        localStorage.setItem('connectify_session_token', handoffToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${handoffToken}`;
        if (handoffUser) {
          localStorage.setItem('connectify_user', handoffUser);
          setUser(JSON.parse(handoffUser));
        } else {
          // No user payload — hydrate it from the token.
          axios.get(`${API}/auth/me`).then((r) => {
            if (r.data?.success && r.data.user) {
              localStorage.setItem('connectify_user', JSON.stringify(r.data.user));
              setUser(r.data.user);
            }
          }).catch(() => {});
        }
        // Strip the sensitive params but keep ?buy so autobuy still fires.
        params.delete('st');
        params.delete('u');
        params.delete('app');
        params.delete('return');
        const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
        window.history.replaceState({}, '', clean);
      }
    } catch (e) { /* ignore malformed handoff */ }

    const savedUser = localStorage.getItem('connectify_user');
    const savedToken = localStorage.getItem('connectify_session_token');

    // Defensive: if a stale user object survives a deploy that added session
    // tokens (pre-2026-07-28 logins issued no token), we would silently send
    // every request without an Authorization header and every admin/user
    // endpoint would 401 — visible as an "empty admin dashboard". Force a
    // clean re-login instead of leaving the user in that broken state.
    if (!handoffToken && savedUser && !savedToken) {
      localStorage.removeItem('connectify_user');
      return;
    }

    if (!handoffToken && savedUser && savedToken) {
      try {
        setUser(JSON.parse(savedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      } catch (e) {
        localStorage.removeItem('connectify_user');
        localStorage.removeItem('connectify_session_token');
      }
    }

    // On any 401 from a protected endpoint (session expired, token revoked,
    // password reset elsewhere), clear the client-side session so the next
    // page load shows the login screen instead of a broken empty UI.
    const interceptorId = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const url = error?.config?.url || '';
        const status = error?.response?.status;
        // Only auto-logout on 401. Ignore auth endpoints themselves (a wrong
        // password shouldn't nuke the session) and public/health endpoints.
        const isAuthEndpoint =
          url.includes('/auth/login') ||
          url.includes('/auth/register') ||
          url.includes('/auth/forgot-password') ||
          url.includes('/auth/reset-password') ||
          url.includes('/auth/verify');
        if (status === 401 && !isAuthEndpoint) {
          const currentPath = window.location.pathname;
          // Never tear down the session while an OAuth sign-in is completing.
          const onOAuthCallback = currentPath.startsWith('/auth/');
          // If there's no session yet, there's nothing to log out of — a 401
          // here is just an anonymous request (e.g. an admin/config probe on a
          // public page) and must not bounce the user to /login.
          const hasSession = !!localStorage.getItem('connectify_session_token');
          // A 401 from an admin-only endpoint means "not an admin", not "your
          // session is invalid". These endpoints (blocked-destinations,
          // *-overrides, …) are fetched for everyone, so their auth failures
          // must never log a normal user out.
          const isAdminProbe = url.includes('/admin/');
          if (hasSession && !isAdminProbe && !onOAuthCallback) {
            localStorage.removeItem('connectify_user');
            localStorage.removeItem('connectify_session_token');
            delete axios.defaults.headers.common['Authorization'];
            // Soft reload if we're inside a protected page so the guard kicks the
            // user to /login. Skip when already on public routes.
            const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/blog'];
            const isPublic = publicPaths.some((p) => currentPath === p || currentPath.startsWith(p + '/'));
            if (!isPublic) {
              window.location.assign('/login');
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  // Load orders for user
  const loadOrders = useCallback(async () => {
    if (user?.email) {
      try {
        const response = await axios.get(`${API}/orders/${user.email}`);
        if (response.data.success) {
          setMySims(response.data.orders.map(order => {
            // Prefer the already-built country object from allCountries (which
            // has the correct regional detection + flag URL from
            // buildCountryFromRaw). Falls back to legacy inline logic if the
            // country isn't in state yet (rare — happens on first render
            // before allCountries has loaded).
            const cid = order.country_id || '';
            const known = allCountries.find(c => c.id === cid);

            let countryObj;
            if (known) {
              countryObj = {
                id: known.id,
                name: nameOverrides[cid] || order.country_name || known.name,
                flag: known.flag,
                isRegion: known.isRegion,
                operators: known.operators || 'Multiple Networks',
              };
            } else {
              const rawId = cid.replace('R_', '').toLowerCase();
              const flagCode = /^[a-z]{2}/.test(rawId) && rawId.substring(0, 2) !== 'ma' && rawId !== 'manual'
                ? rawId.substring(0, 2)
                : (rawId.substring(0, 2) === 'ma' && rawId !== 'manual' ? 'ma' : '');
              const flagUrl = flagCode
                ? `https://flagcdn.com/w160/${flagCode}.png`
                : 'https://flagcdn.com/w160/un.png';
              countryObj = {
                id: cid,
                name: nameOverrides[cid] || order.country_name,
                flag: flagUrl,
                isRegion: false,
                operators: 'Multiple Networks',
              };
            }
            return {
            id: order.id,
            country: countryObj,
            iccid: order.iccid,
            imsi: order.imsi,
            status: order.status,
            roamifyStatus: order.roamify_status,
            expiryDate: order.expiry_date ? new Date(order.expiry_date).toDateString() : null,
            dataTotal: order.data_amount,
            dataUsed: order.data_used || 0,
            purchaseDate: new Date(order.purchase_date).toDateString(),
            planName: order.plan_name,
            isInstalled: order.is_installed,
            lpaCode: order.lpa_code,
            qrCodeUrl: order.qr_code_url,
            iosQuickInstallUrl: order.ios_quick_install_url,
            androidQuickInstallUrl: order.android_quick_install_url,
            smdpAddress: order.smdp_address,
            matchingId: order.matching_id,
            userEmail: order.user_email
            };
          }));
        }
      } catch (e) {
        console.error('Error loading orders:', e);
      }
    } else {
      setMySims([]);
    }
  }, [user, allCountries, nameOverrides]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Fetch exchange rates - only once per day at 12am AWST
  useEffect(() => {
    const fetchRatesIfNeeded = async () => {
      // Skip if already loaded today
      if (ratesLoaded) return;
      
      try {
        // Check cached date
        const cachedDate = localStorage.getItem('connectify_exchange_rates_date');
        const now = new Date();
        const awstTime = new Date(now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60000);
        const todayAWST = awstTime.toISOString().split('T')[0];
        
        // If we have today's rates cached, don't fetch
        if (cachedDate === todayAWST) {
          setRatesLoaded(true);
          return;
        }
        
        // Fetch new rates from server
        const response = await axios.get(`${API}/exchange-rates`);
        if (response.data.success && response.data.rates) {
          const newRates = { ...response.data.rates, USD: 1 };
          setExchangeRates(newRates);
          
          // Cache the rates
          localStorage.setItem('connectify_exchange_rates', JSON.stringify(newRates));
          localStorage.setItem('connectify_exchange_rates_date', response.data.date || todayAWST);
        }
        setRatesLoaded(true);
      } catch (e) {
        console.error('Error fetching rates:', e);
        setRatesLoaded(true);
      }
    };
    
    fetchRatesIfNeeded();
  }, [ratesLoaded]);

  // Currency conversion - IMPORTANT: All prices from applyMarkup are already in AUD
  // This function is kept for backwards compatibility but prices are already in AUD
  const convertPrice = useCallback((audAmount) => {
    // Prices are already in AUD from applyMarkup, no conversion needed for AUD
    if (currentCurrency.code === 'AUD') {
      return audAmount;
    }
    // For other currencies, convert from AUD
    // exchangeRates are USD-based, so we need to convert AUD -> USD -> target currency
    const audToUsd = 1 / 1.55; // Inverse of USD to AUD rate
    const usdAmount = audAmount * audToUsd;
    return usdAmount * (exchangeRates[currentCurrency.code] || 1);
  }, [currentCurrency, exchangeRates]);

  // Convert from selected currency back to AUD (for credit deduction calculations)
  const convertToAud = useCallback((amount) => {
    if (currentCurrency.code === 'AUD') {
      return amount;
    }
    // Reverse the conversion: target currency -> USD -> AUD
    const audToUsd = 1 / 1.55;
    const targetToUsd = 1 / (exchangeRates[currentCurrency.code] || 1);
    const usdAmount = amount * targetToUsd;
    return usdAmount / audToUsd; // USD to AUD
  }, [currentCurrency, exchangeRates]);

  // Format credits - converts AUD credits to display in selected currency
  const formatCredits = useCallback((audCredits) => {
    if (currentCurrency.code === 'AUD') {
      return `A$${audCredits.toFixed(2)}`;
    }
    return `${currentCurrency.symbol}${convertPrice(audCredits).toFixed(2)}`;
  }, [currentCurrency, convertPrice]);

  const formatPrice = useCallback((audAmount) => {
    // Format the price - amount is already in AUD from applyMarkup
    if (currentCurrency.code === 'AUD') {
      return `A$${audAmount.toFixed(2)}`;
    }
    return `${currentCurrency.symbol}${convertPrice(audAmount).toFixed(2)}`;
  }, [currentCurrency, convertPrice]);

  const dismissApiError = useCallback(() => {
    setApiError(null);
  }, []);

  // Map raw API data to country format
  const mapRawToCountry = useCallback((raw) => {
    let id = raw.country_code || raw.countryCode || raw.code || raw.id || raw.iso2;
    const name = raw.country_name || raw.countryName || raw.name || raw.title || '';
    const geography = raw.geography || '';
    
    // Handle duplicate codes - AS is used for both American Samoa (local) and Asia (regional)
    // Create unique IDs for regional plans that share codes with local countries
    if (id === 'AS' && (geography === 'regional' || name === 'Asia')) {
      id = 'ASIA_REG'; // Use unique ID for Asia regional
    }
    
    // Comprehensive country code to region mapping
    const CODE_TO_REGION = {
      // Europe
      AL: 'Europe', AD: 'Europe', AT: 'Europe', BY: 'Europe', BE: 'Europe', BA: 'Europe',
      BG: 'Europe', HR: 'Europe', CY: 'Europe', CZ: 'Europe', DK: 'Europe', EE: 'Europe',
      FI: 'Europe', FR: 'Europe', DE: 'Europe', GR: 'Europe', HU: 'Europe', IS: 'Europe',
      IE: 'Europe', IT: 'Europe', XK: 'Europe', LV: 'Europe', LI: 'Europe', LT: 'Europe',
      LU: 'Europe', MT: 'Europe', MD: 'Europe', MC: 'Europe', ME: 'Europe', NL: 'Europe',
      MK: 'Europe', NO: 'Europe', PL: 'Europe', PT: 'Europe', RO: 'Europe', RU: 'Europe',
      SM: 'Europe', RS: 'Europe', SK: 'Europe', SI: 'Europe', ES: 'Europe', SE: 'Europe',
      CH: 'Europe', UA: 'Europe', GB: 'Europe', VA: 'Europe', AX: 'Europe', FO: 'Europe',
      GI: 'Europe', GG: 'Europe', IM: 'Europe', JE: 'Europe', SJ: 'Europe',
      'GB-SCT': 'Europe', 'PT-20': 'Europe', 'PT-30': 'Europe', 'PT-40': 'Europe',
      NOC: 'Europe', IC: 'Europe', GL: 'Europe',
      
      // Asia
      AF: 'Asia', AM: 'Asia', AZ: 'Asia', BH: 'Asia', BD: 'Asia', BT: 'Asia', BN: 'Asia',
      KH: 'Asia', CN: 'Asia', GE: 'Asia', HK: 'Asia', IN: 'Asia', ID: 'Asia', IR: 'Asia',
      IQ: 'Asia', IL: 'Asia', JP: 'Asia', JO: 'Asia', KZ: 'Asia', KW: 'Asia', KG: 'Asia',
      LA: 'Asia', LB: 'Asia', MO: 'Asia', MY: 'Asia', MV: 'Asia', MN: 'Asia', MM: 'Asia',
      NP: 'Asia', KP: 'Asia', OM: 'Asia', PK: 'Asia', PS: 'Asia', ps: 'Asia', PH: 'Asia',
      QA: 'Asia', SA: 'Asia', SG: 'Asia', KR: 'Asia', LK: 'Asia', SY: 'Asia', TW: 'Asia',
      TJ: 'Asia', TH: 'Asia', TL: 'Asia', TR: 'Asia', TM: 'Asia', AE: 'Asia', UZ: 'Asia',
      VN: 'Asia', YE: 'Asia', CNM: 'Asia', JPL: 'Asia', OMP: 'Asia', AEP: 'Asia', RUP: 'Asia',
      
      // Africa
      DZ: 'Africa', AO: 'Africa', BJ: 'Africa', BW: 'Africa', BF: 'Africa', BI: 'Africa',
      CV: 'Africa', CM: 'Africa', CF: 'Africa', TD: 'Africa', KM: 'Africa', CG: 'Africa',
      CD: 'Africa', DJ: 'Africa', EG: 'Africa', GQ: 'Africa', ER: 'Africa', SZ: 'Africa',
      ET: 'Africa', GA: 'Africa', GM: 'Africa', GH: 'Africa', GN: 'Africa', GW: 'Africa',
      CI: 'Africa', KE: 'Africa', LS: 'Africa', LR: 'Africa', LY: 'Africa', MG: 'Africa',
      MW: 'Africa', ML: 'Africa', MR: 'Africa', MU: 'Africa', YT: 'Africa', MA: 'Africa',
      MZ: 'Africa', NA: 'Africa', NE: 'Africa', NG: 'Africa', RE: 'Africa', RW: 'Africa',
      ST: 'Africa', SN: 'Africa', SC: 'Africa', SL: 'Africa', SO: 'Africa', ZA: 'Africa',
      SS: 'Africa', SD: 'Africa', TZ: 'Africa', TG: 'Africa', TN: 'Africa', UG: 'Africa',
      EH: 'Africa', ZM: 'Africa', ZW: 'Africa', SH: 'Africa', EGP: 'Africa', TNP: 'Africa',
      
      // North America
      US: 'North America', CA: 'North America', MX: 'North America', GT: 'North America',
      BZ: 'North America', SV: 'North America', HN: 'North America', NI: 'North America',
      CR: 'North America', PA: 'North America', BM: 'North America', GL: 'North America',
      PM: 'North America', USL: 'North America', USS: 'North America',
      
      // Caribbean
      AI: 'Caribbean', AG: 'Caribbean', AW: 'Caribbean', BS: 'Caribbean', BB: 'Caribbean',
      BQ: 'Caribbean', VG: 'Caribbean', KY: 'Caribbean', CU: 'Caribbean', CW: 'Caribbean',
      DM: 'Caribbean', DO: 'Caribbean', GD: 'Caribbean', GP: 'Caribbean', HT: 'Caribbean',
      JM: 'Caribbean', MQ: 'Caribbean', MS: 'Caribbean', AN: 'Caribbean', PR: 'Caribbean',
      BL: 'Caribbean', KN: 'Caribbean', LC: 'Caribbean', MF: 'Caribbean', VC: 'Caribbean',
      SX: 'Caribbean', TT: 'Caribbean', TC: 'Caribbean', VI: 'Caribbean',
      'BQ-SA': 'Caribbean', 'BQ-SE': 'Caribbean',
      
      // South America
      AR: 'South America', BO: 'South America', BR: 'South America', CL: 'South America',
      CO: 'South America', EC: 'South America', FK: 'South America', GF: 'South America',
      GY: 'South America', PY: 'South America', PE: 'South America', SR: 'South America',
      UY: 'South America', VE: 'South America',
      
      // Oceania
      AU: 'Oceania', FJ: 'Oceania', KI: 'Oceania', MH: 'Oceania', FM: 'Oceania',
      NR: 'Oceania', NC: 'Oceania', NZ: 'Oceania', NU: 'Oceania', NF: 'Oceania',
      PW: 'Oceania', PG: 'Oceania', WS: 'Oceania', SB: 'Oceania', TO: 'Oceania',
      TV: 'Oceania', VU: 'Oceania', WF: 'Oceania', AS: 'Oceania', CK: 'Oceania',
      PF: 'Oceania', GU: 'Oceania', MP: 'Oceania', CC: 'Oceania', NZP: 'Oceania',
    };
    
    // Regional plan codes mapping
    const REGIONAL_CODES = {
      // Africa
      'AFR': { name: 'Africa', region: 'Africa' },
      // Asia
      'ASIA_REG': { name: 'Asia', region: 'Asia' }, // Remapped from AS to avoid conflict with American Samoa
      'ASY': { name: 'Asia One Year', region: 'Asia' },
      'ASS': { name: 'Asia Sprint', region: 'Asia' },
      'CHE': { name: 'China Explorer', region: 'Asia' },
      'CHEP': { name: 'China Explorer Premium', region: 'Asia' },
      'ASSE': { name: 'Southeast Asia', region: 'Asia' },
      'ASSE4': { name: 'Southeast Asia 4 Countries', region: 'Asia' },
      'ASSEP': { name: 'Southeast Asia Premium', region: 'Asia' },
      // Europe
      'EU': { name: 'Europe', region: 'Europe' },
      'EUL': { name: 'Europe Lite', region: 'Europe' },
      'EUS': { name: 'Europe Sprint', region: 'Europe' },
      'EUUS': { name: 'Europe & United States', region: 'Europe' },
      'BLK': { name: 'Balkans', region: 'Europe' },
      'MED': { name: 'Mediterranean', region: 'Europe' },
      // Middle East
      'MENA': { name: 'Middle East', region: 'Middle East' },
      'MENAP': { name: 'Middle East Premium', region: 'Middle East' },
      // North America
      'NAM': { name: 'North America', region: 'North America' },
      'WTLNA': { name: 'Walk the line - North America', region: 'North America' },
      // South America
      'LATAM': { name: 'South America', region: 'South America' },
      'WTLSA': { name: 'Walk the line - South America', region: 'South America' },
      // Oceania
      'OC': { name: 'Oceania', region: 'Oceania' },
      'AUNZ': { name: 'Australasia', region: 'Oceania' },
      // Caribbean
      'CB': { name: 'Caribbean Islands', region: 'Caribbean' },
      // Global
      'GLO': { name: 'Global', region: 'Global' },
      'GLY': { name: 'Global One Year', region: 'Global' },
      'GLP': { name: 'Global Premium', region: 'Global' },
    };

    // Check if this is a regional plan
    const isRegion = geography === 'regional' || geography === 'global' || REGIONAL_CODES[id];

    // Determine the region
    let region = 'Other';
    if (isRegion && REGIONAL_CODES[id]) {
      region = REGIONAL_CODES[id].region;
    } else if (CODE_TO_REGION[id]) {
      region = CODE_TO_REGION[id];
    }
    // Admin region override wins over the computed region (resolves mismatches).
    if (id && regionOverrides[id]) {
      region = regionOverrides[id];
    }

    // Regional flag URLs — the naive `id.substring(0,2).toLowerCase()` used to
    // pick the flagcdn code produces wrong flags for regionals (e.g. `ASSE`
    // (Southeast Asia) → `as` → 🇦🇸 American Samoa; `MED` → 🇲🇪 Montenegro;
    // `NAM` → 🇳🇦 Namibia; `LATAM` → 🇱🇦 Laos; etc.). We now build the flag
    // URL explicitly per region.
    // • Europe → flagcdn hosts an EU flag under code `eu`.
    // • Everything else → a self-contained SVG data URI showing a globe on a
    //   blue→emerald gradient (matches the Home.js `isRegion` search UI).
    const REGIONAL_GLOBE_SVG_URI = (
      'data:image/svg+xml;utf8,' + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 24">
          <defs>
            <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#3b82f6"/>
              <stop offset="1" stop-color="#10b981"/>
            </linearGradient>
          </defs>
          <rect width="32" height="24" rx="4" fill="url(#g)"/>
          <g fill="none" stroke="#fff" stroke-width="1.4">
            <circle cx="16" cy="12" r="6"/>
            <ellipse cx="16" cy="12" rx="6" ry="2.5"/>
            <line x1="10" y1="12" x2="22" y2="12"/>
            <line x1="16" y1="6" x2="16" y2="18"/>
          </g>
        </svg>`
      )
    );

    let flagUrl;
    if (isRegion && REGIONAL_CODES[id] && REGIONAL_CODES[id].region === 'Europe') {
      // Europe variants (EU, EUL, EUS, EUUS, BLK, MED) → EU flag
      flagUrl = 'https://flagcdn.com/w160/eu.png';
    } else if (isRegion) {
      // All other regional plans → gradient globe icon.
      flagUrl = REGIONAL_GLOBE_SVG_URI;
    } else {
      const flagCode = typeof id === 'string' ? id.replace('R_', '').toLowerCase().substring(0, 2) : 'un';
      flagUrl = `https://flagcdn.com/w160/${flagCode}.png`;
    }

    // Get country-specific image from comprehensive library
    const countryImage = getCountryImage(id);

    return {
      id: id || Math.random().toString(36).substr(2, 9),
      name: (id && nameOverrides[id]) || name,
      region: region,
      flag: flagUrl,
      image: (id && coverOverrides[id]) || countryImage,
      startPrice: raw.startPrice || 4.99,
      operators: raw.operators || 'Multiple Local Network Operators',
      description: raw.description || `High-speed 4G/5G eSIM for ${name}.`,
      isRegion: isRegion,
      tags: (id && destinationTags[id]) || []
    };
  }, [nameOverrides, regionOverrides, destinationTags, coverOverrides]);

  // Blocked destinations state - loaded from backend
  const [blockedDestinations, setBlockedDestinations] = useState(['Middle East', 'Middle East Premium']);

  // Fetch blocked destinations from backend
  useEffect(() => {
    const fetchBlockedDestinations = async () => {
      try {
        const response = await axios.get(`${API}/admin/blocked-destinations`);
        if (response.data.success) {
          setBlockedDestinations(response.data.blocked_destinations || []);
        }
      } catch (err) {
        console.log('Using default blocked destinations');
      }
    };
    fetchBlockedDestinations();
  }, []);

  // Filter function to remove blocked destinations
  const filterBlockedDestinations = useCallback((countries) => {
    return countries.filter(country => 
      !blockedDestinations.some(blocked => 
        country.id === blocked || 
        country.name.toLowerCase() === blocked.toLowerCase()
      )
    );
  }, [blockedDestinations]);

  // Initialize countries - load mock data immediately, then refresh from API
  const initializeApi = useCallback(async () => {
    // First, load from mock data for instant display
    const mockCountries = getMockCountries();
    setAllCountries(filterBlockedDestinations(mockCountries));
    setLoadingCountries(false);
    
    // Then try to refresh from API in background
    try {
      const response = await axios.get(`${API}/countries`);
      if (response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const mapped = response.data.data.map(mapRawToCountry);
        // Dedupe by id (the API can return the same code twice, e.g. GLP) so
        // React row keys stay unique and lists don't double-render.
        const seen = new Set();
        const liveCountries = mapped
          .filter((c) => (c.id && !seen.has(c.id) ? (seen.add(c.id), true) : false))
          .sort((a, b) => a.name.localeCompare(b.name));
        setAllCountries(filterBlockedDestinations(liveCountries));
        setApiError(null);
      }
    } catch (e) {
      console.warn("API: Using cached data", e.message);
      // Already using mock data, no action needed
    }
  }, [mapRawToCountry, filterBlockedDestinations]);

  useEffect(() => { 
    initializeApi(); 
  }, [initializeApi]);

  // Fetch admin-defined destination groups so the destinations list can
  // collapse variant members into a single visible card.
  const refreshDestinationGroups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/destination-groups`);
      if (r.data?.success) setDestinationGroups(r.data.groups || []);
    } catch (e) {
      /* silent — grouping is best-effort */
    }
  }, []);
  useEffect(() => { refreshDestinationGroups(); }, [refreshDestinationGroups]);

  // Starting prices map — used by group cards to show "from $X" across the
  // cheapest member.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/starting-prices`).then((r) => {
      if (!cancelled && r.data?.success) setStartingPrices(r.data.prices || {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Apply markup to prices based on geography and price tier grid
  // NOTE: basePrice is in USD from API
  // Returns price in USD with markup applied - currency conversion happens in formatPrice
  const applyMarkup = useCallback((basePrice, countryId) => {
    // Convert to AUD for tier calculation only (must match server's USD_TO_AUD_RATE = 1.55)
    const basePriceAud = basePrice * 1.55;
    
    // Determine geography type - must match server-side logic exactly
    let geoType = 'local';
    if (countryId === 'R_GL' || countryId?.toLowerCase() === 'global') {
      geoType = 'global';
    } else if (countryId?.startsWith('R_') || ['EU', 'ASIA_REG', 'ASS', 'EUS', 'EUL'].includes(countryId)) {
      geoType = 'regional';
    }

    // Determine price tier based on AUD price (must match server logic)
    let priceTier = '20_plus';
    if (basePriceAud < 3) {
      priceTier = '0_3';
    } else if (basePriceAud < 6) {
      priceTier = '3_6';
    } else if (basePriceAud < 10) {
      priceTier = '6_10';
    } else if (basePriceAud < 15) {
      priceTier = '10_15';
    } else if (basePriceAud < 20) {
      priceTier = '15_20';
    }

    // Get the markup from the grid
    const markupKey = `${geoType}_${priceTier}`;
    const markupPercentage = markupSettings[markupKey] || 40; // Default to 40% if not found

    // Apply markup to AUD price and return in AUD
    // This is the final price in AUD that should be displayed directly
    // formatPrice will be updated to handle AUD prices correctly
    return basePriceAud * (1 + (markupPercentage / 100));
  }, [markupSettings]);

  // Fetch plans for a country
  const fetchPlansForCountry = useCallback(async (countryId) => {
    if (livePlanCache[countryId]) return livePlanCache[countryId];

    // Pass the countryId directly - backend handles the mapping and filtering
    // ASIA_REG is sent as-is, backend maps it to 'AS' and filters for regional geography

    try {
      const response = await axios.get(`${API}/packages/${countryId}`);
      if (response.data.success) {
        const responseData = response.data.data;
        
        // Extract signals/carriers metadata for the destination
        const signals = responseData?.signals || [];
        const geography = responseData?.geography || 'local';
        
        // Store destination metadata (signals, carriers, countries covered)
        if (signals.length > 0) {
          const meta = {
            geography,
            signals,
            // For local: extract carriers from the country's signal
            carriers: geography === 'local' 
              ? [...new Set(signals.flatMap(s => s.carriers || []))]
              : [],
            // For regional: extract country names
            countriesCovered: geography === 'regional' || geography === 'global'
              ? signals.map(s => s.name).filter(Boolean)
              : []
          };
          setDestinationMetaCache(prev => ({ ...prev, [countryId]: meta }));
        }
        
        const collectedItems = [];
        
        const traverseAndCollect = (node) => {
          if (!node || typeof node !== 'object') return;
          if (Array.isArray(node)) {
            for (const item of node) traverseAndCollect(item);
            return;
          }
          if (node.package !== undefined && node.package !== null) {
            collectedItems.push(node);
            return;
          }
          for (const key in node) {
            if (Object.prototype.hasOwnProperty.call(node, key)) traverseAndCollect(node[key]);
          }
        };
        
        traverseAndCollect(responseData);

        const apiPlans = collectedItems.map((item, index) => {
          if (!item || typeof item !== 'object') return null;
          const rawId = item.package_code || item.packageId || item.id || `plan_${index}_${Date.now()}`;
          const packageId = String(rawId);
          const pkgName = item.package || item.name || '';
          const pkgNameUpper = String(pkgName).toUpperCase();
          
          let dataVal = parseFloat(item.data_amount) || parseFloat(item.data) || 0;
          const apiUnlimited = item.is_unlimited === true || item.unlimited === true;
          let isUnl = apiUnlimited;

          if (pkgNameUpper) {
            // Parse numeric data amounts from the name. Accept TB / GB / MB.
            const tbMatch = pkgNameUpper.match(/(\d+(?:\.\d+)?)\s*TB/);
            const gbMatch = pkgNameUpper.match(/(\d+(?:\.\d+)?)\s*GB/);
            const mbMatch = !gbMatch && !tbMatch
              ? pkgNameUpper.match(/(\d+(?:\.\d+)?)\s*MB/)
              : null;

            let parsedData = 0;
            if (gbMatch) parsedData = parseFloat(gbMatch[1]);
            else if (tbMatch) parsedData = parseFloat(tbMatch[1]) * 1000;
            else if (mbMatch) parsedData = parseFloat(mbMatch[1]) / 1000;

            // Decide unlimited-data vs fixed-data carefully.
            // "Unlimited Data" → truly unlimited data (even with throttle parenthetical like "(2 GB / 384 KBPS)").
            // A fixed prefix like "30 GB, Unlimited Call & SMS" → fixed data; "UNLIMITED" qualifies calls/SMS only.
            const hasUnlimitedDataPhrase = /UNLIMITED\s+DATA/.test(pkgNameUpper);
            const hasUnlimitedWord = pkgNameUpper.includes('UNLIMITED');
            const unlimitedQualifiesCallsOrSms = /UNLIMITED\s+(CALL|CALLS|TEXT|TEXTS|SMS|MIN|MINS|MINUTE|MINUTES|TALK|VOICE)/.test(pkgNameUpper);
            // Detect a leading fixed data prefix: "30 GB," or "1 TB," etc. at the start.
            const hasLeadingFixedData = /^\s*\d+(?:\.\d+)?\s*(GB|TB|MB)\b/.test(pkgNameUpper);

            if (apiUnlimited || hasUnlimitedDataPhrase) {
              isUnl = true;
            } else if (hasLeadingFixedData && parsedData > 0) {
              // Fixed-data plan; any "UNLIMITED" in the name refers to call/SMS.
              isUnl = false;
              dataVal = parsedData;
            } else if (hasUnlimitedWord && !unlimitedQualifiesCallsOrSms && parsedData === 0) {
              isUnl = true;
            } else if (parsedData > 0 && dataVal === 0) {
              dataVal = parsedData;
            }
          }
          if (dataVal === 0 && !isUnl) dataVal = 1;
          
          let durationVal = parseInt(item.duration) || parseInt(item.validity) || parseInt(item.days) || 0;
          if (durationVal === 0 && pkgNameUpper) {
            const d = pkgNameUpper.match(/(\d+)\s*(D|DAY|DAYS)/);
            if (d) durationVal = parseInt(d[1]);
          }
          if (durationVal === 0) durationVal = 7;
          
          let costPrice = parseFloat(item.price) || parseFloat(item.retail_price) || 0;
          if (costPrice === 0) costPrice = 5.00;

          const finalPrice = applyMarkup(costPrice, countryId);
          
          let unlimitedDetails = isUnl ? 'High Speed Data' : '';
          if (isUnl) {
            const throttleMatch = pkgName.match(/\((\d+(?:\.\d+)?)\s*GB\s*\/\s*(\d+\s*[KM]BPS)\)/i);
            if (throttleMatch) {
              unlimitedDetails = `${throttleMatch[2]} after ${throttleMatch[1]}GB/Day`;
            }
          }
          
          return {
            id: packageId,
            packageId: packageId,
            name: pkgName || `${dataVal}GB Plan`,
            dataAmount: isUnl ? 999 : dataVal,
            durationDays: durationVal,
            price: finalPrice,
            isUnlimited: isUnl,
            operator: item.operator || 'Multiple Networks',
            signal: '5G/LTE/4G',
            unlimitedDetails: unlimitedDetails,
            // Call and SMS info
            withCall: item.withCall || false,
            withSMS: item.withSMS || false,
            callAmount: item.callAmount || 0,
            callUnit: item.callUnit || 'mins',
            smsAmount: item.smsAmount || 0,
            smsUnit: item.smsUnit || 'sms',
            plan: item.plan || 'data', // 'data', 'data-voice', 'data-voice-sms'
            originalData: item
          };
        }).filter(p => p !== null);

        // Cache the result (even if empty) - API successfully returned data
        setLivePlanCache(prev => ({ ...prev, [countryId]: apiPlans }));
        return apiPlans;
      }
      
      // API returned success: false - use mock plans as fallback
      return getMockPlans(countryId).map(p => ({ ...p, price: applyMarkup(p.price, countryId) }));
    } catch (e) {
      console.log(`Using offline plans for ${countryId}`);
      return getMockPlans(countryId).map(p => ({ ...p, price: applyMarkup(p.price, countryId) }));
    }
  }, [livePlanCache, applyMarkup]);

  // Purchase plan via Roamify
  const purchasePlan = async (packageId) => {
    try {
      const response = await axios.post(`${API}/esim/order`, {
        packageId: packageId,
        referenceId: `REF-${Date.now()}-${Math.floor(Math.random()*1000)}`
      });
      return response.data.data;
    } catch (e) {
      throw new Error(e.response?.data?.detail || 'Order failed');
    }
  };

  // Apply eSIM
  const applyEsim = async (esimId) => {
    try {
      const response = await axios.post(`${API}/esim/apply`, { esimId: esimId });
      return response.data.data;
    } catch (e) {
      throw new Error(e.response?.data?.detail || 'eSIM activation failed');
    }
  };

  // Auth functions
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        setUser(response.data.user);
        localStorage.setItem('connectify_user', JSON.stringify(response.data.user));
        if (response.data.session_token) {
          localStorage.setItem('connectify_session_token', response.data.session_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.session_token}`;
        }
        // Native shell: register FCM/APNS device with backend so push
        // notifications can target this user (no-op on web).
        registerPushForUser(response.data.user?.email);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // Google OAuth login - called from AuthCallback after successful OAuth
  const loginWithGoogle = async (userData, sessionToken) => {
    try {
      setUser(userData);
      localStorage.setItem('connectify_user', JSON.stringify(userData));
      if (sessionToken) {
        localStorage.setItem('connectify_session_token', sessionToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
      }
      registerPushForUser(userData?.email);
      return true;
    } catch (e) {
      return false;
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      if (response.data.success) {
        setUser(response.data.user);
        localStorage.setItem('connectify_user', JSON.stringify(response.data.user));
        if (response.data.session_token) {
          localStorage.setItem('connectify_session_token', response.data.session_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.session_token}`;
        }
        registerPushForUser(response.data.user?.email);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // Legacy: verifyUser kept as a no-op shim so old callers don't crash.
  // The new secure flow is forgotPassword → email link → resetPasswordWithToken.
  const verifyUser = async () => false;

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      return !!response.data.success;
    } catch (e) {
      return false;
    }
  };

  const resetPasswordWithToken = async (token, newPassword) => {
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token, newPassword
      });
      return !!response.data.success;
    } catch (e) {
      return false;
    }
  };

  // Legacy resetPassword signature (email, date, newPass) is no longer
  // supported — the date-based reset was a security hole (SEC-003). This
  // shim returns false so callers surface an error and can be migrated.
  const resetPassword = async () => false;

  const logout = () => {
    // Stop pushes for this user's device (no-op on web).
    unregisterPushForUser(user?.email);
    setUser(null);
    localStorage.removeItem('connectify_user');
    localStorage.removeItem('connectify_session_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Order management
  const addSim = async (country, plan, apiData = {}) => {
    if (!user) return;
    
    try {
      const response = await axios.post(`${API}/orders`, {
        country_id: country.id,
        country_name: country.name,
        plan_id: plan.id,
        plan_name: plan.name,
        data_amount: plan.dataAmount,
        duration_days: plan.durationDays,
        price: plan.price,
        user_email: user.email,
        payment_session_id: apiData.sessionId || ''
      });
      
      if (response.data.success) {
        const order = response.data.order;
        const newSim = {
          id: order.id,
          country: country,
          iccid: order.iccid,
          imsi: order.imsi,
          status: 'ACTIVE',
          expiryDate: new Date(order.expiry_date).toDateString(),
          dataTotal: plan.dataAmount,
          dataUsed: 0,
          purchaseDate: new Date().toDateString(),
          planName: plan.name,
          isInstalled: false,
          ...apiData,
          userEmail: user.email
        };
        setMySims(prev => [newSim, ...prev]);
      }
    } catch (e) {
      console.error('Error creating order:', e);
    }
  };

  const topUpSim = (id, plan) => {
    setMySims(prev => prev.map(s => 
      s.id === id ? { ...s, dataTotal: s.dataTotal + plan.dataAmount } : s
    ));
  };

  const installSim = async (id) => {
    try {
      await axios.patch(`${API}/orders/${id}/install`);
      setMySims(prev => prev.map(s => s.id === id ? { ...s, isInstalled: true } : s));
    } catch (e) {
      console.error('Error marking installed:', e);
    }
  };

  // Promo codes
  const addPromoCode = async (code) => {
    try {
      const response = await axios.post(`${API}/promo-codes`, code);
      if (response.data.success) {
        setPromoCodes(prev => [...prev, response.data.code]);
      }
    } catch (e) {
      console.error('Error adding promo:', e);
    }
  };

  const deletePromoCode = async (id) => {
    try {
      await axios.delete(`${API}/promo-codes/${id}`);
      setPromoCodes(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      console.error('Error deleting promo:', e);
    }
  };

  const getDiscountForCode = async (code, userEmail = null, planType = 'local', countryCode = '') => {
    try {
      const response = await axios.post(`${API}/promo-codes/validate`, { 
        code,
        user_email: userEmail || user?.email || '',
        plan_type: planType,
        country_code: countryCode
      });
      // Return full response for referral and discount handling
      return {
        success: response.data.success,
        discount: response.data.discount || 0,
        localDiscount: response.data.localDiscount,
        regionalDiscount: response.data.regionalDiscount,
        isReferral: response.data.isReferral || false,
        referrerName: response.data.referrerName || null,
        referrerEmail: response.data.referrerEmail || null,
        message: response.data.message || ''
      };
    } catch (e) {
      return { success: false, discount: 0, isReferral: false, message: 'Error validating code' };
    }
  };

  // Referral functions
  const getUserReferralInfo = async (email) => {
    try {
      const response = await axios.get(`${API}/referral/user/${email}`);
      return response.data;
    } catch (e) {
      console.error('Error fetching referral info:', e);
      return null;
    }
  };

  const recordReferral = async (referralCode, referredEmail, orderTotal) => {
    try {
      const response = await axios.post(`${API}/referral/record`, {
        referral_code: referralCode,
        referred_email: referredEmail,
        order_total: orderTotal
      });
      return response.data;
    } catch (e) {
      console.error('Error recording referral:', e);
      return { success: false };
    }
  };

  const completeReferral = async (referredEmail, orderId) => {
    try {
      const response = await axios.post(`${API}/referral/complete`, {
        referred_email: referredEmail,
        order_id: orderId
      });
      return response.data;
    } catch (e) {
      console.error('Error completing referral:', e);
      return { success: false };
    }
  };

  const getUserCredits = async (email) => {
    try {
      const response = await axios.get(`${API}/user/credits/${email}`);
      return response.data;
    } catch (e) {
      console.error('Error fetching credits:', e);
      return { creditBalance: 0, transactions: [] };
    }
  };

  const applyUserCredits = async (email, amount, orderId) => {
    try {
      const response = await axios.post(`${API}/user/credits/apply`, {
        user_email: email,
        amount: amount,
        order_id: orderId
      });
      return response.data;
    } catch (e) {
      console.error('Error applying credits:', e);
      return { success: false };
    }
  };

  const getReferralSettings = async () => {
    try {
      const response = await axios.get(`${API}/referral/settings`);
      return response.data.settings;
    } catch (e) {
      return { credit_value_aud: 5.0, referred_discount: 0.10, is_active: true };
    }
  };

  const updateReferralSettings = async (settings) => {
    try {
      const response = await axios.post(`${API}/referral/settings`, settings);
      return response.data.success;
    } catch (e) {
      console.error('Error updating referral settings:', e);
      return false;
    }
  };

  const getAdminReferrals = async () => {
    try {
      const response = await axios.get(`${API}/admin/referrals`);
      return response.data;
    } catch (e) {
      console.error('Error fetching admin referrals:', e);
      return { referrals: [], stats: {} };
    }
  };

  const addUserCredits = async (email, amount, reason) => {
    try {
      const response = await axios.post(`${API}/user/credits/add`, {
        user_email: email,
        amount: amount,
        reason: reason
      });
      return response.data;
    } catch (e) {
      console.error('Error adding credits:', e);
      return { success: false };
    }
  };

  // Admin functions
  const getAllUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      return response.data.users || [];
    } catch (e) {
      return [];
    }
  }, []);

  const getAllOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      return response.data.orders || [];
    } catch (e) {
      return [];
    }
  }, []);

  const updateMarkupSettings = async (settings) => {
    try {
      await axios.post(`${API}/markup-settings`, settings);
      setMarkupSettings(settings);
      setLivePlanCache({});
    } catch (e) {
      console.error('Error updating markup:', e);
    }
  };

  // Load promo codes and markup on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [promoRes, markupRes, overridesRes, regionRes, tagsRes, coverRes] = await Promise.all([
          axios.get(`${API}/promo-codes`),
          axios.get(`${API}/markup-settings`),
          axios.get(`${API}/country-name-overrides`),
          axios.get(`${API}/region-overrides`),
          axios.get(`${API}/destination-tags`),
          axios.get(`${API}/cover-image-overrides`),
        ]);
        if (promoRes.data.success) setPromoCodes(promoRes.data.codes);
        if (markupRes.data.success) setMarkupSettings(markupRes.data.settings);
        if (overridesRes.data.success) setNameOverrides(overridesRes.data.overrides || {});
        if (regionRes.data.success) setRegionOverrides(regionRes.data.overrides || {});
        if (tagsRes.data.success) setDestinationTags(tagsRes.data.tags || {});
        if (coverRes.data.success) setCoverOverrides(coverRes.data.overrides || {});
      } catch (e) {
        console.error('Error loading settings:', e);
      } finally {
        setMarkupLoaded(true);
      }
    };
    loadSettings();
  }, []);

  // ============ NOTIFICATIONS ============
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    if (!user?.email) return [];
    try {
      const res = await axios.get(`${API}/notifications/${user.email}`);
      if (res.data.success) {
        setNotifications(res.data.notifications);
        return res.data.notifications;
      }
    } catch (e) {
      console.error('Error fetching notifications:', e);
    }
    return [];
  }, [user]);

  const markNotificationsRead = useCallback(async (ids) => {
    if (!user?.email) return;
    try {
      await axios.post(`${API}/notifications/mark-read`, {
        user_email: user.email,
        notification_ids: ids || []
      });
      setNotifications([]);
    } catch (e) {
      console.error('Error marking notifications read:', e);
    }
  }, [user]);

  // Fetch notifications when user logs in
  useEffect(() => {
    if (user?.email) {
      fetchNotifications();
    }
  }, [user, fetchNotifications]);

  // ============ PROMO ANALYTICS ============
  const getPromoAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-analytics`);
      return res.data;
    } catch (e) {
      console.error('Error fetching promo analytics:', e);
      return null;
    }
  }, []);

  const getFeaturedDestinations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/featured-destinations`);
      return res.data;
    } catch (e) {
      console.error('Error fetching featured destinations:', e);
      return null;
    }
  }, []);

  const setFeaturedDestinations = useCallback(async (destinations) => {
    try {
      const res = await axios.post(`${API}/admin/featured-destinations`, { destinations });
      return res.data;
    } catch (e) {
      console.error('Error setting featured destinations:', e);
      return null;
    }
  }, []);

  return (
    <AppContext.Provider value={{
      user, setUser, login, loginWithGoogle, register, verifyUser,
      resetPassword, forgotPassword, resetPasswordWithToken, logout,
      mySims, addSim, topUpSim, installSim, refreshSims: loadOrders, allCountries,
      loadingCountries, fetchPlansForCountry, purchasePlan, applyEsim,
      destinationGroups, refreshDestinationGroups, startingPrices,
      apiError, dismissApiError, currentCurrency, setCurrentCurrency, formatPrice, convertPrice, convertToAud, formatCredits,
      markupLoaded,
      nameOverrides,
      refreshNameOverrides: async () => {
        try {
          const r = await axios.get(`${API}/country-name-overrides`);
          if (r.data.success) setNameOverrides(r.data.overrides || {});
        } catch (e) { /* silent */ }
      },
      regionOverrides,
      refreshRegionOverrides: async () => {
        try {
          const r = await axios.get(`${API}/region-overrides`);
          if (r.data.success) setRegionOverrides(r.data.overrides || {});
        } catch (e) { /* silent */ }
      },
      destinationTags,
      refreshDestinationTags: async () => {
        try {
          const r = await axios.get(`${API}/destination-tags`);
          if (r.data.success) setDestinationTags(r.data.tags || {});
        } catch (e) { /* silent */ }
      },
      isAdmin, promoCodes, addPromoCode, deletePromoCode, getDiscountForCode, getAllUsers, getAllOrders,
      markupSettings, updateMarkupSettings, CURRENCIES, destinationMetaCache,
      // Referral functions
      getUserReferralInfo, recordReferral, completeReferral, getUserCredits, applyUserCredits,
      getReferralSettings, updateReferralSettings, getAdminReferrals, addUserCredits,
      // Notifications
      notifications, fetchNotifications, markNotificationsRead,
      // Promo Analytics & Featured Destinations
      getPromoAnalytics, getFeaturedDestinations, setFeaturedDestinations
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within an AppProvider');
  return context;
};

// All countries data from Roamify API
const ALL_COUNTRIES_RAW = [
  { code: 'AF', name: 'Afghanistan', geography: 'local' },
  { code: 'AFR', name: 'Africa', geography: 'regional' },
  { code: 'AX', name: 'Aland Island', geography: 'local' },
  { code: 'AL', name: 'Albania', geography: 'local' },
  { code: 'DZ', name: 'Algeria', geography: 'local' },
  { code: 'AD', name: 'Andorra', geography: 'local' },
  { code: 'AO', name: 'Angola', geography: 'local' },
  { code: 'AI', name: 'Anguilla', geography: 'local' },
  { code: 'AG', name: 'Antigua And Barbuda', geography: 'local' },
  { code: 'AN', name: 'Antilles', geography: 'local' },
  { code: 'AR', name: 'Argentina', geography: 'local' },
  { code: 'AM', name: 'Armenia', geography: 'local' },
  { code: 'AW', name: 'Aruba', geography: 'local' },
  { code: 'ASR', name: 'Asia', geography: 'regional' },
  { code: 'ASY', name: 'Asia One Year', geography: 'regional' },
  { code: 'ASS', name: 'Asia Sprint', geography: 'regional' },
  { code: 'AUNZ', name: 'Australasia', geography: 'regional' },
  { code: 'AU', name: 'Australia', geography: 'local' },
  { code: 'AT', name: 'Austria', geography: 'local' },
  { code: 'AZ', name: 'Azerbaijan', geography: 'local' },
  { code: 'BS', name: 'Bahamas', geography: 'local' },
  { code: 'BH', name: 'Bahrain', geography: 'local' },
  { code: 'BLK', name: 'Balkans', geography: 'regional' },
  { code: 'BD', name: 'Bangladesh', geography: 'local' },
  { code: 'BB', name: 'Barbados', geography: 'local' },
  { code: 'BY', name: 'Belarus', geography: 'local' },
  { code: 'BE', name: 'Belgium', geography: 'local' },
  { code: 'BZ', name: 'Belize', geography: 'local' },
  { code: 'BJ', name: 'Benin', geography: 'local' },
  { code: 'BM', name: 'Bermuda', geography: 'local' },
  { code: 'BT', name: 'Bhutan', geography: 'local' },
  { code: 'BO', name: 'Bolivia', geography: 'local' },
  { code: 'BQ', name: 'Bonaire', geography: 'local' },
  { code: 'BA', name: 'Bosnia and Herzegovina', geography: 'local' },
  { code: 'BW', name: 'Botswana', geography: 'local' },
  { code: 'BR', name: 'Brazil', geography: 'local' },
  { code: 'VG', name: 'British Virgin Islands', geography: 'local' },
  { code: 'BN', name: 'Brunei', geography: 'local' },
  { code: 'BG', name: 'Bulgaria', geography: 'local' },
  { code: 'BF', name: 'Burkina Faso', geography: 'local' },
  { code: 'BI', name: 'Burundi', geography: 'local' },
  { code: 'KH', name: 'Cambodia', geography: 'local' },
  { code: 'CM', name: 'Cameroon', geography: 'local' },
  { code: 'CA', name: 'Canada', geography: 'local' },
  { code: 'IC', name: 'Canary Islands', geography: 'local' },
  { code: 'CV', name: 'Cape Verde', geography: 'local' },
  { code: 'CB', name: 'Caribbean Islands', geography: 'regional' },
  { code: 'KY', name: 'Cayman Islands', geography: 'local' },
  { code: 'CF', name: 'Central African Republic', geography: 'local' },
  { code: 'TD', name: 'Chad', geography: 'local' },
  { code: 'CL', name: 'Chile', geography: 'local' },
  { code: 'CN', name: 'China', geography: 'local' },
  { code: 'CHE', name: 'China Explorer', geography: 'regional' },
  { code: 'CHEP', name: 'China Explorer Premium', geography: 'regional' },
  { code: 'CNM', name: 'China Mainland', geography: 'local' },
  { code: 'CC', name: 'Cocos (Keeling) Islands', geography: 'local' },
  { code: 'CO', name: 'Colombia', geography: 'local' },
  { code: 'KM', name: 'Comoros', geography: 'local' },
  { code: 'CG', name: 'Congo', geography: 'local' },
  { code: 'CK', name: 'Cook Islands', geography: 'local' },
  { code: 'CR', name: 'Costa Rica', geography: 'local' },
  { code: 'HR', name: 'Croatia', geography: 'local' },
  { code: 'CU', name: 'Cuba', geography: 'local' },
  { code: 'CW', name: 'Curacao', geography: 'local' },
  { code: 'CY', name: 'Cyprus', geography: 'local' },
  { code: 'CZ', name: 'Czech Republic', geography: 'local' },
  { code: 'CD', name: 'Democratic Republic of the Congo', geography: 'local' },
  { code: 'DK', name: 'Denmark', geography: 'local' },
  { code: 'DJ', name: 'Djibouti', geography: 'local' },
  { code: 'DM', name: 'Dominica', geography: 'local' },
  { code: 'DO', name: 'Dominican Republic', geography: 'local' },
  { code: 'EC', name: 'Ecuador', geography: 'local' },
  { code: 'EG', name: 'Egypt', geography: 'local' },
  { code: 'SV', name: 'El Salvador', geography: 'local' },
  { code: 'GQ', name: 'Equatorial Guinea', geography: 'local' },
  { code: 'ER', name: 'Eritrea', geography: 'local' },
  { code: 'EE', name: 'Estonia', geography: 'local' },
  { code: 'SZ', name: 'Eswatini', geography: 'local' },
  { code: 'ET', name: 'Ethiopia', geography: 'local' },
  { code: 'EU', name: 'Europe', geography: 'regional' },
  { code: 'EUL', name: 'Europe Lite', geography: 'regional' },
  { code: 'EUS', name: 'Europe Sprint', geography: 'regional' },
  { code: 'EUUS', name: 'Europe & United States', geography: 'regional' },
  { code: 'FK', name: 'Falkland Islands', geography: 'local' },
  { code: 'FO', name: 'Faroe Islands', geography: 'local' },
  { code: 'FJ', name: 'Fiji', geography: 'local' },
  { code: 'FI', name: 'Finland', geography: 'local' },
  { code: 'FR', name: 'France', geography: 'local' },
  { code: 'GF', name: 'French Guiana', geography: 'local' },
  { code: 'PF', name: 'French Polynesia', geography: 'local' },
  { code: 'GA', name: 'Gabon', geography: 'local' },
  { code: 'GM', name: 'Gambia', geography: 'local' },
  { code: 'GE', name: 'Georgia', geography: 'local' },
  { code: 'DE', name: 'Germany', geography: 'local' },
  { code: 'GH', name: 'Ghana', geography: 'local' },
  { code: 'GI', name: 'Gibraltar', geography: 'local' },
  { code: 'GLO', name: 'Global', geography: 'global' },
  { code: 'GLY', name: 'Global One Year', geography: 'global' },
  { code: 'GLP', name: 'Global Premium', geography: 'global' },
  { code: 'GR', name: 'Greece', geography: 'local' },
  { code: 'GL', name: 'Greenland', geography: 'local' },
  { code: 'GD', name: 'Grenada', geography: 'local' },
  { code: 'GP', name: 'Guadeloupe', geography: 'local' },
  { code: 'GU', name: 'Guam', geography: 'local' },
  { code: 'GT', name: 'Guatemala', geography: 'local' },
  { code: 'GG', name: 'Guernsey', geography: 'local' },
  { code: 'GW', name: 'Guinea Bissau', geography: 'local' },
  { code: 'GN', name: 'Guinea', geography: 'local' },
  { code: 'GY', name: 'Guyana', geography: 'local' },
  { code: 'HT', name: 'Haiti', geography: 'local' },
  { code: 'HN', name: 'Honduras', geography: 'local' },
  { code: 'HK', name: 'Hong Kong', geography: 'local' },
  { code: 'HU', name: 'Hungary', geography: 'local' },
  { code: 'IS', name: 'Iceland', geography: 'local' },
  { code: 'IN', name: 'India', geography: 'local' },
  { code: 'ID', name: 'Indonesia', geography: 'local' },
  { code: 'IR', name: 'Iran', geography: 'local' },
  { code: 'IQ', name: 'Iraq', geography: 'local' },
  { code: 'IE', name: 'Ireland', geography: 'local' },
  { code: 'IM', name: 'Isle of Man', geography: 'local' },
  { code: 'IL', name: 'Israel', geography: 'local' },
  { code: 'IT', name: 'Italy', geography: 'local' },
  { code: 'CI', name: 'Ivory Coast', geography: 'local' },
  { code: 'JM', name: 'Jamaica', geography: 'local' },
  { code: 'JP', name: 'Japan', geography: 'local' },
  { code: 'JE', name: 'Jersey', geography: 'local' },
  { code: 'JO', name: 'Jordan', geography: 'local' },
  { code: 'KZ', name: 'Kazakhstan', geography: 'local' },
  { code: 'KE', name: 'Kenya', geography: 'local' },
  { code: 'KI', name: 'Kiribati', geography: 'local' },
  { code: 'XK', name: 'Kosovo', geography: 'local' },
  { code: 'KW', name: 'Kuwait', geography: 'local' },
  { code: 'KG', name: 'Kyrgyzstan', geography: 'local' },
  { code: 'LA', name: 'Laos', geography: 'local' },
  { code: 'LV', name: 'Latvia', geography: 'local' },
  { code: 'LB', name: 'Lebanon', geography: 'local' },
  { code: 'LS', name: 'Lesotho', geography: 'local' },
  { code: 'LR', name: 'Liberia', geography: 'local' },
  { code: 'LY', name: 'Libya', geography: 'local' },
  { code: 'LI', name: 'Liechtenstein', geography: 'local' },
  { code: 'LT', name: 'Lithuania', geography: 'local' },
  { code: 'LU', name: 'Luxembourg', geography: 'local' },
  { code: 'MO', name: 'Macau', geography: 'local' },
  { code: 'MG', name: 'Madagascar', geography: 'local' },
  { code: 'MW', name: 'Malawi', geography: 'local' },
  { code: 'MY', name: 'Malaysia', geography: 'local' },
  { code: 'MV', name: 'Maldives', geography: 'local' },
  { code: 'ML', name: 'Mali', geography: 'local' },
  { code: 'MT', name: 'Malta', geography: 'local' },
  { code: 'MH', name: 'Marshall Islands', geography: 'local' },
  { code: 'MQ', name: 'Martinique', geography: 'local' },
  { code: 'MR', name: 'Mauritania', geography: 'local' },
  { code: 'MU', name: 'Mauritius', geography: 'local' },
  { code: 'YT', name: 'Mayotte', geography: 'local' },
  { code: 'MED', name: 'Mediterranean', geography: 'regional' },
  { code: 'MX', name: 'Mexico', geography: 'local' },
  { code: 'FM', name: 'Micronesia', geography: 'local' },
  { code: 'MENA', name: 'Middle East', geography: 'regional' },
  { code: 'MENAP', name: 'Middle East Premium', geography: 'regional' },
  { code: 'MD', name: 'Moldova', geography: 'local' },
  { code: 'MC', name: 'Monaco', geography: 'local' },
  { code: 'MN', name: 'Mongolia', geography: 'local' },
  { code: 'ME', name: 'Montenegro', geography: 'local' },
  { code: 'MS', name: 'Montserrat', geography: 'local' },
  { code: 'MA', name: 'Morocco', geography: 'local' },
  { code: 'MZ', name: 'Mozambique', geography: 'local' },
  { code: 'MM', name: 'Myanmar', geography: 'local' },
  { code: 'NA', name: 'Namibia', geography: 'local' },
  { code: 'NR', name: 'Nauru', geography: 'local' },
  { code: 'NP', name: 'Nepal', geography: 'local' },
  { code: 'NL', name: 'Netherlands', geography: 'local' },
  { code: 'NC', name: 'New Caledonia', geography: 'local' },
  { code: 'NZ', name: 'New Zealand', geography: 'local' },
  { code: 'NI', name: 'Nicaragua', geography: 'local' },
  { code: 'NE', name: 'Niger', geography: 'local' },
  { code: 'NG', name: 'Nigeria', geography: 'local' },
  { code: 'NU', name: 'Niue', geography: 'local' },
  { code: 'NF', name: 'Norfolk Island', geography: 'local' },
  { code: 'NAM', name: 'North America', geography: 'regional' },
  { code: 'MK', name: 'North Macedonia', geography: 'local' },
  { code: 'NOC', name: 'Northern Cyprus', geography: 'local' },
  { code: 'MP', name: 'Northern Mariana Islands', geography: 'local' },
  { code: 'NO', name: 'Norway', geography: 'local' },
  { code: 'OC', name: 'Oceania', geography: 'regional' },
  { code: 'OM', name: 'Oman', geography: 'local' },
  { code: 'PK', name: 'Pakistan', geography: 'local' },
  { code: 'PW', name: 'Palau', geography: 'local' },
  { code: 'PS', name: 'Palestine', geography: 'local' },
  { code: 'PA', name: 'Panama', geography: 'local' },
  { code: 'PG', name: 'Papua New Guinea', geography: 'local' },
  { code: 'PY', name: 'Paraguay', geography: 'local' },
  { code: 'PE', name: 'Peru', geography: 'local' },
  { code: 'PH', name: 'Philippines', geography: 'local' },
  { code: 'PL', name: 'Poland', geography: 'local' },
  { code: 'PT', name: 'Portugal', geography: 'local' },
  { code: 'PR', name: 'Puerto Rico', geography: 'local' },
  { code: 'QA', name: 'Qatar', geography: 'local' },
  { code: 'RE', name: 'Réunion', geography: 'local' },
  { code: 'RO', name: 'Romania', geography: 'local' },
  { code: 'RU', name: 'Russia', geography: 'local' },
  { code: 'RW', name: 'Rwanda', geography: 'local' },
  { code: 'BL', name: 'Saint Barthélemy', geography: 'local' },
  { code: 'SH', name: 'Saint Helena', geography: 'local' },
  { code: 'KN', name: 'Saint Kitts and Nevis', geography: 'local' },
  { code: 'LC', name: 'Saint Lucia', geography: 'local' },
  { code: 'MF', name: 'Saint Martin', geography: 'local' },
  { code: 'PM', name: 'Saint Pierre and Miquelon', geography: 'local' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', geography: 'local' },
  { code: 'WS', name: 'Samoa', geography: 'local' },
  { code: 'SM', name: 'San Marino', geography: 'local' },
  { code: 'ST', name: 'São Tomé and Príncipe', geography: 'local' },
  { code: 'SA', name: 'Saudi Arabia', geography: 'local' },
  { code: 'SN', name: 'Senegal', geography: 'local' },
  { code: 'RS', name: 'Serbia', geography: 'local' },
  { code: 'SC', name: 'Seychelles', geography: 'local' },
  { code: 'SL', name: 'Sierra Leone', geography: 'local' },
  { code: 'SG', name: 'Singapore', geography: 'local' },
  { code: 'SX', name: 'Sint Maarten', geography: 'local' },
  { code: 'SK', name: 'Slovakia', geography: 'local' },
  { code: 'SI', name: 'Slovenia', geography: 'local' },
  { code: 'SB', name: 'Solomon Islands', geography: 'local' },
  { code: 'SO', name: 'Somalia', geography: 'local' },
  { code: 'ZA', name: 'South Africa', geography: 'local' },
  { code: 'LATAM', name: 'South America', geography: 'regional' },
  { code: 'KR', name: 'South Korea', geography: 'local' },
  { code: 'SS', name: 'South Sudan', geography: 'local' },
  { code: 'ASSE4', name: 'Southeast Asia 4 Countries', geography: 'regional' },
  { code: 'ASSE', name: 'Southeast Asia', geography: 'regional' },
  { code: 'ASSEP', name: 'Southeast Asia Premium', geography: 'regional' },
  { code: 'ES', name: 'Spain', geography: 'local' },
  { code: 'LK', name: 'Sri Lanka', geography: 'local' },
  { code: 'SD', name: 'Sudan', geography: 'local' },
  { code: 'SR', name: 'Suriname', geography: 'local' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen', geography: 'local' },
  { code: 'SE', name: 'Sweden', geography: 'local' },
  { code: 'CH', name: 'Switzerland', geography: 'local' },
  { code: 'SY', name: 'Syria', geography: 'local' },
  { code: 'TW', name: 'Taiwan', geography: 'local' },
  { code: 'TJ', name: 'Tajikistan', geography: 'local' },
  { code: 'TZ', name: 'Tanzania', geography: 'local' },
  { code: 'TH', name: 'Thailand', geography: 'local' },
  { code: 'TL', name: 'Timor-Leste', geography: 'local' },
  { code: 'TG', name: 'Togo', geography: 'local' },
  { code: 'TO', name: 'Tonga', geography: 'local' },
  { code: 'TT', name: 'Trinidad and Tobago', geography: 'local' },
  { code: 'TN', name: 'Tunisia', geography: 'local' },
  { code: 'TR', name: 'Turkey', geography: 'local' },
  { code: 'TM', name: 'Turkmenistan', geography: 'local' },
  { code: 'TC', name: 'Turks and Caicos Islands', geography: 'local' },
  { code: 'TV', name: 'Tuvalu', geography: 'local' },
  { code: 'UG', name: 'Uganda', geography: 'local' },
  { code: 'UA', name: 'Ukraine', geography: 'local' },
  { code: 'AE', name: 'United Arab Emirates', geography: 'local' },
  { code: 'GB', name: 'United Kingdom', geography: 'local' },
  { code: 'US', name: 'United States', geography: 'local' },
  { code: 'UY', name: 'Uruguay', geography: 'local' },
  { code: 'VI', name: 'US Virgin Islands', geography: 'local' },
  { code: 'UZ', name: 'Uzbekistan', geography: 'local' },
  { code: 'VU', name: 'Vanuatu', geography: 'local' },
  { code: 'VA', name: 'Vatican City', geography: 'local' },
  { code: 'VE', name: 'Venezuela', geography: 'local' },
  { code: 'VN', name: 'Vietnam', geography: 'local' },
  { code: 'WTLNA', name: 'Walk the line - North America', geography: 'regional' },
  { code: 'WTLSA', name: 'Walk the line - South America', geography: 'regional' },
  { code: 'WF', name: 'Wallis and Futuna', geography: 'local' },
  { code: 'EH', name: 'Western Sahara', geography: 'local' },
  { code: 'YE', name: 'Yemen', geography: 'local' },
  { code: 'ZM', name: 'Zambia', geography: 'local' },
  { code: 'ZW', name: 'Zimbabwe', geography: 'local' },
];

// Mock data fallbacks - uses pre-defined countries with proper region mapping
function getMockCountries() {
  // Region mapping
  const CODE_TO_REGION = {
    AL: 'Europe', AD: 'Europe', AT: 'Europe', BY: 'Europe', BE: 'Europe', BA: 'Europe',
    BG: 'Europe', HR: 'Europe', CY: 'Europe', CZ: 'Europe', DK: 'Europe', EE: 'Europe',
    FI: 'Europe', FR: 'Europe', DE: 'Europe', GR: 'Europe', HU: 'Europe', IS: 'Europe',
    IE: 'Europe', IT: 'Europe', XK: 'Europe', LV: 'Europe', LI: 'Europe', LT: 'Europe',
    LU: 'Europe', MT: 'Europe', MD: 'Europe', MC: 'Europe', ME: 'Europe', NL: 'Europe',
    MK: 'Europe', NO: 'Europe', PL: 'Europe', PT: 'Europe', RO: 'Europe', RU: 'Europe',
    SM: 'Europe', RS: 'Europe', SK: 'Europe', SI: 'Europe', ES: 'Europe', SE: 'Europe',
    CH: 'Europe', UA: 'Europe', GB: 'Europe', VA: 'Europe', AX: 'Europe', FO: 'Europe',
    GI: 'Europe', GG: 'Europe', IM: 'Europe', JE: 'Europe', SJ: 'Europe', NOC: 'Europe',
    IC: 'Europe', GL: 'Europe',
    AF: 'Asia', AM: 'Asia', AZ: 'Asia', BH: 'Asia', BD: 'Asia', BT: 'Asia', BN: 'Asia',
    KH: 'Asia', CN: 'Asia', GE: 'Asia', HK: 'Asia', IN: 'Asia', ID: 'Asia', IR: 'Asia',
    IQ: 'Asia', IL: 'Asia', JP: 'Asia', JO: 'Asia', KZ: 'Asia', KW: 'Asia', KG: 'Asia',
    LA: 'Asia', LB: 'Asia', MO: 'Asia', MY: 'Asia', MV: 'Asia', MN: 'Asia', MM: 'Asia',
    NP: 'Asia', OM: 'Asia', PK: 'Asia', PS: 'Asia', PH: 'Asia', QA: 'Asia', SA: 'Asia',
    SG: 'Asia', KR: 'Asia', LK: 'Asia', SY: 'Asia', TW: 'Asia', TJ: 'Asia', TH: 'Asia',
    TL: 'Asia', TR: 'Asia', TM: 'Asia', AE: 'Asia', UZ: 'Asia', VN: 'Asia', YE: 'Asia',
    CNM: 'Asia',
    DZ: 'Africa', AO: 'Africa', BJ: 'Africa', BW: 'Africa', BF: 'Africa', BI: 'Africa',
    CV: 'Africa', CM: 'Africa', CF: 'Africa', TD: 'Africa', KM: 'Africa', CG: 'Africa',
    CD: 'Africa', DJ: 'Africa', EG: 'Africa', GQ: 'Africa', ER: 'Africa', SZ: 'Africa',
    ET: 'Africa', GA: 'Africa', GM: 'Africa', GH: 'Africa', GN: 'Africa', GW: 'Africa',
    CI: 'Africa', KE: 'Africa', LS: 'Africa', LR: 'Africa', LY: 'Africa', MG: 'Africa',
    MW: 'Africa', ML: 'Africa', MR: 'Africa', MU: 'Africa', YT: 'Africa', MA: 'Africa',
    MZ: 'Africa', NA: 'Africa', NE: 'Africa', NG: 'Africa', RE: 'Africa', RW: 'Africa',
    ST: 'Africa', SN: 'Africa', SC: 'Africa', SL: 'Africa', SO: 'Africa', ZA: 'Africa',
    SS: 'Africa', SD: 'Africa', TZ: 'Africa', TG: 'Africa', TN: 'Africa', UG: 'Africa',
    EH: 'Africa', ZM: 'Africa', ZW: 'Africa', SH: 'Africa',
    US: 'North America', CA: 'North America', MX: 'North America', GT: 'North America',
    BZ: 'North America', SV: 'North America', HN: 'North America', NI: 'North America',
    CR: 'North America', PA: 'North America', BM: 'North America', PM: 'North America',
    AI: 'Caribbean', AG: 'Caribbean', AW: 'Caribbean', BS: 'Caribbean', BB: 'Caribbean',
    BQ: 'Caribbean', VG: 'Caribbean', KY: 'Caribbean', CU: 'Caribbean', CW: 'Caribbean',
    DM: 'Caribbean', DO: 'Caribbean', GD: 'Caribbean', GP: 'Caribbean', HT: 'Caribbean',
    JM: 'Caribbean', MQ: 'Caribbean', MS: 'Caribbean', AN: 'Caribbean', PR: 'Caribbean',
    BL: 'Caribbean', KN: 'Caribbean', LC: 'Caribbean', MF: 'Caribbean', VC: 'Caribbean',
    SX: 'Caribbean', TT: 'Caribbean', TC: 'Caribbean', VI: 'Caribbean',
    AR: 'South America', BO: 'South America', BR: 'South America', CL: 'South America',
    CO: 'South America', EC: 'South America', FK: 'South America', GF: 'South America',
    GY: 'South America', PY: 'South America', PE: 'South America', SR: 'South America',
    UY: 'South America', VE: 'South America',
    AU: 'Oceania', FJ: 'Oceania', KI: 'Oceania', MH: 'Oceania', FM: 'Oceania',
    NR: 'Oceania', NC: 'Oceania', NZ: 'Oceania', NU: 'Oceania', NF: 'Oceania',
    PW: 'Oceania', PG: 'Oceania', WS: 'Oceania', SB: 'Oceania', TO: 'Oceania',
    TV: 'Oceania', VU: 'Oceania', WF: 'Oceania', CK: 'Oceania', PF: 'Oceania',
    GU: 'Oceania', MP: 'Oceania', CC: 'Oceania', AS: 'Oceania',
  };

  const REGIONAL_CODES = {
    AFR: 'Africa', ASR: 'Asia', ASIA_REG: 'Asia', ASY: 'Asia', ASS: 'Asia', CHE: 'Asia', CHEP: 'Asia',
    ASSE: 'Asia', ASSE4: 'Asia', ASSEP: 'Asia', EU: 'Europe', EUL: 'Europe',
    EUS: 'Europe', EUUS: 'Europe', BLK: 'Europe', MED: 'Europe', MENA: 'Middle East',
    MENAP: 'Middle East', NAM: 'North America', WTLNA: 'North America',
    LATAM: 'South America', WTLSA: 'South America', OC: 'Oceania', AUNZ: 'Oceania',
    CB: 'Caribbean', GLO: 'Global', GLY: 'Global', GLP: 'Global',
  };

  // Country images now handled by imported getCountryImage function

  // Regional globe icon (matches AppContext main path). Used for regional
  // codes where flagcdn either has no representation (Asia, LATAM, Africa,
  // etc.) or would return a wrong national flag due to the naive
  // id.substring(0,2) heuristic (e.g. ASSE → 🇦🇸 American Samoa).
  const REGIONAL_GLOBE_SVG_URI = (
    'data:image/svg+xml;utf8,' + encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 24">
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#3b82f6"/>
            <stop offset="1" stop-color="#10b981"/>
          </linearGradient>
        </defs>
        <rect width="32" height="24" rx="4" fill="url(#g)"/>
        <g fill="none" stroke="#fff" stroke-width="1.4">
          <circle cx="16" cy="12" r="6"/>
          <ellipse cx="16" cy="12" rx="6" ry="2.5"/>
          <line x1="10" y1="12" x2="22" y2="12"/>
          <line x1="16" y1="6" x2="16" y2="18"/>
        </g>
      </svg>`
    )
  );

  return ALL_COUNTRIES_RAW.map(raw => {
    const id = raw.code;
    const name = raw.name;
    const isRegion = raw.geography === 'regional' || raw.geography === 'global';
    let region = 'Other';
    if (isRegion && REGIONAL_CODES[id]) region = REGIONAL_CODES[id];
    else if (CODE_TO_REGION[id]) region = CODE_TO_REGION[id];
    let flag;
    if (isRegion && REGIONAL_CODES[id] === 'Europe') {
      flag = 'https://flagcdn.com/w160/eu.png';
    } else if (isRegion) {
      flag = REGIONAL_GLOBE_SVG_URI;
    } else {
      const flagCode = id.toLowerCase().substring(0, 2);
      flag = `https://flagcdn.com/w160/${flagCode}.png`;
    }
    const image = getCountryImage(id);
    return { id, name, region, flag, image, isRegion };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

function getMockPlans(countryId) {
  const seed = countryId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const basePrice = (seed % 5) + 3;
  
  return [
    { id: `p_1gb_7_${countryId}`, packageId: `p_1gb_7_${countryId}`, name: '1GB Plan', dataAmount: 1, durationDays: 7, price: basePrice, isUnlimited: false, operator: 'Multiple Networks', signal: '5G/LTE/4G' },
    { id: `p_3gb_30_${countryId}`, packageId: `p_3gb_30_${countryId}`, name: '3GB Plan', dataAmount: 3, durationDays: 30, price: basePrice * 2.5, isUnlimited: false, operator: 'Multiple Networks', signal: '5G/LTE/4G' },
    { id: `p_5gb_30_${countryId}`, packageId: `p_5gb_30_${countryId}`, name: '5GB Plan', dataAmount: 5, durationDays: 30, price: basePrice * 3.8, isUnlimited: false, operator: 'Multiple Networks', signal: '5G/LTE/4G' },
    { id: `p_10gb_30_${countryId}`, packageId: `p_10gb_30_${countryId}`, name: '10GB Plan', dataAmount: 10, durationDays: 30, price: basePrice * 6.5, isUnlimited: false, operator: 'Multiple Networks', signal: '5G/LTE/4G' },
    { id: `p_unl_30_${countryId}`, packageId: `p_unl_30_${countryId}`, name: 'Unlimited Plan', dataAmount: 999, durationDays: 30, price: basePrice * 12, isUnlimited: true, operator: 'Multiple Networks', signal: '5G/LTE/4G' }
  ];
}

export { CURRENCIES };
